import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Platform,
  Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';

interface InviteModalProps {
  visible: boolean;
  onClose: () => void;
  inviteLink: string;
  onCopySuccess?: () => void;
}

const InviteModal: React.FC<InviteModalProps> = ({
  visible,
  onClose,
  inviteLink,
  onCopySuccess,
}) => {
  const { colors } = useTheme();
  const [showToast, setShowToast] = useState(false);
  const [toastAnim] = useState(new Animated.Value(200)); // Start off-screen to the right

  useEffect(() => {
    if (showToast) {
      // Slide in from right (useNativeDriver: false – avoids missing native module warning on web/some native builds)
      Animated.timing(toastAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: false,
      }).start(() => {
        setTimeout(() => {
          Animated.timing(toastAnim, {
            toValue: 200,
            duration: 300,
            useNativeDriver: false,
          }).start(() => {
            setShowToast(false);
          });
        }, 2000);
      });
    } else {
      toastAnim.setValue(200);
    }
  }, [showToast, toastAnim]);

  const copyToClipboard = async () => {
    try {
      if (
        Platform.OS === 'web' &&
        typeof navigator !== 'undefined' &&
        navigator.clipboard
      ) {
        await navigator.clipboard.writeText(inviteLink);
      } else if (Platform.OS !== 'web') {
        try {
          // @ts-ignore - Native-only module
          const Clipboard =
            require('@react-native-clipboard/clipboard').default;
          if (Clipboard && Clipboard.setString) {
            Clipboard.setString(inviteLink);
          }
        } catch {
          // Clipboard not available
        }
      }
      setShowToast(true);
      onCopySuccess?.();
    } catch {
      // Copy failed silently
    }
  };

  // Debug logging
  useEffect(() => {
    if (visible) {
      // Do nothing
    }
  }, [visible, inviteLink]);

  return (
    <Modal
      visible={visible}
      transparent={Platform.OS === 'web'}
      animationType={Platform.OS === 'web' ? 'fade' : 'slide'}
      presentationStyle={Platform.OS === 'web' ? undefined : 'pageSheet'}
      onRequestClose={onClose}
      statusBarTranslucent={Platform.OS !== 'web'}
    >
      <View style={styles.overlay}>
        <View
          style={[
            styles.modalContainer,
            { backgroundColor: colors.background },
          ]}
        >
          <View style={styles.header}>
            <Text style={[styles.title, { color: colors.text }]}>
              Invite Friends
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          <View style={styles.content}>
            <Text style={[styles.description, { color: colors.secondary }]}>
              Share this link with your friends to invite them to ColourSense:
            </Text>

            {inviteLink ? (
              <View
                style={[
                  styles.linkContainer,
                  { backgroundColor: colors.card, borderColor: colors.border },
                ]}
              >
                <Text
                  style={[styles.linkText, { color: colors.text }]}
                  numberOfLines={1}
                  ellipsizeMode="middle"
                >
                  {inviteLink}
                </Text>
                <TouchableOpacity
                  onPress={copyToClipboard}
                  style={[
                    styles.copyButton,
                    { backgroundColor: colors.primary },
                  ]}
                >
                  <Ionicons name="copy-outline" size={20} color="#ffffff" />
                </TouchableOpacity>
              </View>
            ) : (
              <View
                style={[
                  styles.linkContainer,
                  { backgroundColor: colors.card, borderColor: colors.border },
                ]}
              >
                <Text style={[styles.linkText, { color: colors.secondary }]}>
                  Generating invite link...
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Toast Notification - Outside modal, at bottom right of screen */}
        {showToast && (
          <Animated.View
            style={[
              styles.toastContainer,
              {
                transform: [{ translateX: toastAnim }],
              },
            ]}
          >
            <View
              style={[styles.toastContent, { backgroundColor: colors.primary }]}
            >
              <Ionicons name="checkmark-circle" size={20} color="white" />
              <Text style={styles.toastText}>Link copied to clipboard!</Text>
            </View>
          </Animated.View>
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    ...(Platform.OS === 'web' && {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
    }),
  },
  modalContainer: {
    width: '100%',
    maxWidth: 500,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    ...(Platform.OS === 'web' && {
      position: 'relative' as const,
      zIndex: 10001,
    }),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
  },
  closeButton: {
    padding: 4,
  },
  content: {
    gap: 16,
  },
  description: {
    fontSize: 16,
    lineHeight: 22,
  },
  linkContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 12,
  },
  linkText: {
    flex: 1,
    fontSize: 14,
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'monospace',
  },
  copyButton: {
    padding: 8,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  toastContainer: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    zIndex: 10002,
    ...(Platform.OS === 'web' && {
      position: 'fixed' as const,
    }),
  },
  toastContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    gap: 8,
  },
  toastText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '500',
  },
});

export default InviteModal;
